// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/PcpOrdens.tsx
//  PCP — Listagem de Ordens de Produção + Sugestões + Planejamento
// ════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Plus, Trash2, Factory, PlayCircle, CheckCircle2,
  Eye, Pencil, PauseCircle, XCircle,
  Lightbulb, CalendarDays, AlertTriangle, Printer,
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { usePcp } from '@/features/operacional/hooks/usePcp';
import { pcpService } from '@/features/operacional/services/pcpService';
import { etapaProducaoService } from '@/features/operacional/services/etapaProducaoService';
import { useEstoqueReposicao } from '@/features/estoque/hooks/useEstoquePosicao';
import { calcularStatusPcp } from '@/features/operacional/types';
import type { PcpFiltros, PcpOrdemResumo, StatusPcp } from '@/features/operacional/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { BarraFiltros } from '@/components/BarraFiltros';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { SortableTableHead } from '@/components/SortableTableHead';
import { alternarOrdenacao, type DirecaoOrdenacao } from '@/lib/consultas';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { Label } from '@/components/ui/label';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// ── Helpers de status ─────────────────────────────────────────────────────

const statusConfig: Record<StatusPcp, { label: string; className: string; icon: typeof Factory }> = {
  aberta:       { label: 'Aberta',       className: 'bg-blue-500/10 text-blue-500',    icon: Factory      },
  em_andamento: { label: 'Em Andamento', className: 'bg-orange-500/10 text-orange-500',icon: PlayCircle   },
  pausada:      { label: 'Pausada',      className: 'bg-yellow-500/10 text-yellow-600',icon: PauseCircle  },
  finalizada:   { label: 'Finalizada',   className: 'bg-green-500/10 text-green-500',  icon: CheckCircle2 },
  cancelada:    { label: 'Cancelada',    className: 'bg-red-500/10 text-red-500',      icon: XCircle      },
};

// ── Aba: Sugestões de Produção ────────────────────────────────────────────

function TabSugestoes() {
  const navigate = useNavigate();
  const { data: reposicao, isLoading } = useEstoqueReposicao();

  const sugestoes = reposicao ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-yellow-500" />
          Sugestões de Produção — Estoque Abaixo do Mínimo
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-center py-8 text-muted-foreground">Carregando...</p>
        ) : sugestoes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-green-500" />
            <p>Todos os produtos estão acima do estoque mínimo.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {sugestoes.length} produto{sugestoes.length !== 1 ? 's' : ''} abaixo do estoque mínimo.
              Crie uma Ordem de Produção para suprir a necessidade.
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead>Local</TableHead>
                    <TableHead className="text-right">Saldo Atual</TableHead>
                    <TableHead className="text-right">Estoque Mínimo</TableHead>
                    <TableHead className="text-right">Qtd. Sugerida</TableHead>
                    <TableHead className="text-right">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sugestoes.map((s) => (
                    <TableRow key={`${s.produtoId}-${s.localEstoqueId}`}>
                      <TableCell className="font-medium">{s.descricao}</TableCell>
                      <TableCell className="text-muted-foreground">{s.localDescricao}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        {s.saldoAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {s.estoqueMinimo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-orange-600">
                        {s.quantidadeRepor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          onClick={() => navigate('/operacional/pcp/nova', {
                            state: {
                              sugestao: {
                                produtoId:   s.produtoId,
                                descricao:   s.descricao,
                                quantidade:  s.quantidadeRepor,
                                referencia:  `Reposição: ${s.descricao}`,
                              },
                            },
                          })}
                        >
                          <Plus className="h-3 w-3" />Criar Ordem
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Aba: Planejamento por Período ─────────────────────────────────────────

const mesIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

function TabPlanejamento() {
  const navigate = useNavigate();
  const hoje = new Date();
  const inicioDefault = mesIso(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
  const fimDefault    = mesIso(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1));

  const [mesInicio, setMesInicio] = useState(inicioDefault);
  const [mesFim,    setMesFim]    = useState(fimDefault);

  // Busca no servidor TODAS as ordens com entrega no intervalo de meses (antes filtrava
  // só as 200 mais recentes no cliente e as demais sumiam do calendário).
  const inicio = `${mesInicio}-01`;
  const [anoFim, mFim] = mesFim.split('-').map(Number);
  const fim = `${mesFim}-${String(new Date(anoFim, mFim, 0).getDate()).padStart(2, '0')}`;
  const { data: ordensFiltradas = [], isLoading } = useQuery({
    queryKey: ['pcp', 'planejamento', inicio, fim],
    queryFn: () => pcpService.getPorPeriodo(inicio, fim, 'entrega'),
    enabled: !!mesInicio && !!mesFim && inicio <= fim,
  });

  const porMes = useMemo(() => {
    const map = new Map<string, PcpOrdemResumo[]>();
    ordensFiltradas.forEach((o) => {
      const dt  = new Date(o.dtPrevisaoEntrega);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(o);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [ordensFiltradas]);

  const nomeMes = (ym: string) => {
    const [y, m] = ym.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />Planejamento por Período — Previsão de Entrega
            </CardTitle>
            <div className="flex items-center gap-2">
              <Input type="month" value={mesInicio} onChange={(e) => setMesInicio(e.target.value)} className="w-36 h-8 text-sm" />
              <span className="text-muted-foreground text-sm">até</span>
              <Input type="month" value={mesFim}    onChange={(e) => setMesFim(e.target.value)}    className="w-36 h-8 text-sm" />
            </div>
          </div>
        </CardHeader>
      </Card>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">Carregando...</CardContent>
        </Card>
      ) : porMes.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Nenhuma ordem com previsão de entrega no período selecionado.
          </CardContent>
        </Card>
      ) : (
        porMes.map(([mes, itens]) => {
          const abertas      = itens.filter((o) => !o.isIniciado && !o.isFinalizado && !o.isCancelado).length;
          const emAndamento  = itens.filter((o) => o.isIniciado && !o.isFinalizado && !o.isCancelado).length;
          const finalizadas  = itens.filter((o) => o.isFinalizado).length;
          const canceladas   = itens.filter((o) => o.isCancelado).length;
          return (
            <Card key={mes}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base capitalize">{nomeMes(mes)}</CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    {abertas     > 0 && <Badge className="bg-blue-500/10 text-blue-500">{abertas} abertas</Badge>}
                    {emAndamento > 0 && <Badge className="bg-orange-500/10 text-orange-500">{emAndamento} em andamento</Badge>}
                    {finalizadas > 0 && <Badge className="bg-green-500/10 text-green-500">{finalizadas} finalizadas</Badge>}
                    {canceladas  > 0 && <Badge className="bg-red-500/10 text-red-500">{canceladas} canceladas</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nº</TableHead>
                      <TableHead>Referência</TableHead>
                      <TableHead>Prev. Início</TableHead>
                      <TableHead>Prev. Entrega</TableHead>
                      <TableHead className="text-right">Qtd. Prevista</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itens.map((o) => {
                      const status = calcularStatusPcp(o);
                      const cfg    = statusConfig[status];
                      const Icon   = cfg.icon;
                      return (
                        <TableRow key={o.id}>
                          <TableCell className="font-medium">#{o.numeroOrdem}</TableCell>
                          <TableCell>{o.referencia || <span className="text-muted-foreground">—</span>}</TableCell>
                          <TableCell>{new Date(o.dtPrevisaoInicio).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>{new Date(o.dtPrevisaoEntrega).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell className="text-right">{o.quantidadePrevista}</TableCell>
                          <TableCell>
                            <Badge className={cfg.className}>
                              <span className="flex items-center gap-1"><Icon className="h-3 w-3" />{cfg.label}</span>
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/operacional/pcp/${o.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}

// ── Aba: Listagem de Ordens ───────────────────────────────────────────────

const FILTROS_VAZIOS: PcpFiltros = {};

type CampoPcp = 'numero' | 'emissao' | 'entrega';

function contarFiltros(f: PcpFiltros) {
  return Object.values(f).filter((v) => v !== undefined && v !== '').length;
}

function TabOrdens({ filtros, setFiltros }: {
  filtros: PcpFiltros;
  setFiltros: (f: PcpFiltros) => void;
}) {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Ordenação no servidor (v118): Nº, emissão e previsão de entrega.
  const [ordenacao, setOrdenacao] = useState<{ ordenarPor: CampoPcp; direcao: DirecaoOrdenacao }>(
    { ordenarPor: 'emissao', direcao: 'desc' });
  const pag = usePaginacaoServidor({ resetarQuando: [filtros, ordenacao] });
  const { ordens, total, totalPaginas, isLoading, isFetching, deleteOrdem } =
    usePcp({ pagina: pag.pagina, tamanhoPagina: pag.tamanhoPagina, filtros: { ...filtros, ...ordenacao } });

  const { data: etapas = [] } = useQuery({
    queryKey: ['etapas-producao', 'todas'],
    queryFn: () => etapaProducaoService.getAll(true),
  });

  const cabecalho = {
    ...ordenacao,
    onOrdenar: (campo: CampoPcp) => setOrdenacao(alternarOrdenacao(ordenacao, campo, 'desc')),
  };

  const set = (patch: PcpFiltros) => setFiltros({ ...filtros, ...patch });

  return (
    <>
      <Card>
        <CardHeader className="space-y-4">
          <CardTitle>Listagem de Ordens de Produção</CardTitle>
          <BarraFiltros
            busca={filtros.busca ?? ''}
            onBuscar={(t) => set({ busca: t || undefined })}
            placeholderBusca="Nº ou referência..."
            filtrosAtivos={contarFiltros(filtros)}
            onLimpar={() => setFiltros(FILTROS_VAZIOS)}
          >
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={filtros.status ?? 'todos'}
                onValueChange={(v) => set({ status: v === 'todos' ? undefined : v })}>
                <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aberta">Aberta</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="pausada">Pausada</SelectItem>
                  <SelectItem value="finalizada">Finalizada</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FiltroPeriodo
              rotulo="Prev. Entrega"
              inicio={filtros.de ?? ''}
              fim={filtros.ate ?? ''}
              onChange={(p) => set({ de: p.inicio || undefined, ate: p.fim || undefined })}
            />
            <div className="space-y-1 w-60">
              <Label className="text-xs text-muted-foreground">Cliente</Label>
              <PessoaSearchCombobox
                key={filtros.clienteId ?? 'sem-cliente'}
                value={filtros.clienteId}
                onChange={(id) => set({ clienteId: id })}
                filter="cliente"
                placeholder="Todos os clientes"
              />
            </div>
            <div className="space-y-1 w-60">
              <Label className="text-xs text-muted-foreground">Produto</Label>
              <ProdutoSearchCombobox
                key={filtros.produtoId ?? 'sem-produto'}
                value={filtros.produtoId}
                onChange={(id) => set({ produtoId: id })}
                placeholder="Todos os produtos"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Etapa</Label>
              <Select value={filtros.etapaId ? String(filtros.etapaId) : '__none__'}
                onValueChange={(v) => set({ etapaId: v === '__none__' ? undefined : Number(v) })}>
                <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Etapa" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Todas as etapas</SelectItem>
                  {etapas.map((et) => (
                    <SelectItem key={et.id} value={String(et.id)}>
                      {et.descricao ?? `Etapa ${et.sequencia}`}{et.isInativo ? ' (inativa)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </BarraFiltros>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead campo="numero" {...cabecalho}>Nº</SortableTableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Referência</TableHead>
                  <SortableTableHead campo="emissao" {...cabecalho}>Emissão</SortableTableHead>
                  <SortableTableHead campo="entrega" {...cabecalho}>Prev. Entrega</SortableTableHead>
                  <TableHead className="text-right">Qtd. Prevista</TableHead>
                  <TableHead className="text-right">Custo Previsto</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : ordens.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    {filtros.de || filtros.ate
                      ? 'Nenhuma ordem neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma ordem encontrada'}
                  </TableCell></TableRow>
                ) : (
                  ordens.map((o: PcpOrdemResumo) => {
                    const status = calcularStatusPcp(o);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    const podeEditar = !o.isFinalizado && !o.isCancelado;
                    const outros = (o.totalItens ?? 0) - 1;
                    return (
                      <TableRow key={o.id}>
                        <TableCell className="font-medium">{o.numeroOrdem}</TableCell>
                        <TableCell>
                          {o.produtoDescricao ?? <span className="text-muted-foreground">—</span>}
                          {outros > 0 && (
                            <span className="ml-1 text-xs text-muted-foreground">+{outros} {outros === 1 ? 'item' : 'itens'}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {o.isPedidoInterno || !o.clienteNome
                            ? <span className="text-muted-foreground">Pedido interno</span>
                            : o.clienteNome}
                        </TableCell>
                        <TableCell>{o.referencia || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell>{new Date(o.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>{new Date(o.dtPrevisaoEntrega).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell className="text-right">{o.quantidadePrevista}</TableCell>
                        <TableCell className="text-right font-medium">
                          {o.custoPrevisto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1"><Icon className="h-3 w-3" />{cfg.label}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {podeEditar && (
                              <Button size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/pcp/${o.id}`)} title="Editar">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" title="Ver detalhes"
                              onClick={() => navigate(`/operacional/pcp/${o.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" title="Imprimir ordem de produção"
                              onClick={() => pcpService.abrirPdf(o.id).catch((err) =>
                                toast.error('Não foi possível gerar o PDF da ordem.', { description: getApiErrorMessage(err) }))}>
                              <Printer className="h-4 w-4" />
                            </Button>
                            {!o.isIniciado && !o.isFinalizado && (
                              <Button size="sm" variant="ghost" onClick={() => setDeletingId(o.id)} title="Excluir">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="pt-2 border-t">
            <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />
            {isFetching && !isLoading && <p className="text-xs text-muted-foreground mt-1">(atualizando...)</p>}
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteOrdem(deletingId); setDeletingId(null); } }}
        title="Excluir Ordem de Produção"
        description="Tem certeza que deseja excluir esta ordem de produção?"
      />
    </>
  );
}

// ── Componente principal ──────────────────────────────────────────────────

const PcpOrdens = () => {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState<PcpFiltros>(FILTROS_VAZIOS);

  // KPIs calculados no servidor sobre TODAS as ordens filtradas (status não entra:
  // os cards são justamente a contagem por status).
  const filtrosKpi = useMemo(() => {
    const { status: _status, ...resto } = filtros;
    return resto;
  }, [filtros]);
  const { data: kpis, isLoading } = useQuery({
    queryKey: ['pcp', 'kpis', filtrosKpi],
    queryFn: () => pcpService.getKpis(filtrosKpi),
  });

  const totalAbertas     = kpis?.abertas ?? 0;
  const totalEmAndamento = (kpis?.emAndamento ?? 0) + (kpis?.pausadas ?? 0);
  const totalFinalizadas = kpis?.finalizadas ?? 0;
  const custoPrevTotal   = kpis?.custoPrevistoAtivas ?? 0;

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">PCP — Produção</h1>
          <p className="text-muted-foreground mt-1">Planejamento e Controle da Produção</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/pcp/nova')}>
          <Plus className="h-4 w-4" />Nova Ordem
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Factory className="h-4 w-4" />Abertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{isLoading ? '—' : totalAbertas}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando início</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PlayCircle className="h-4 w-4" />Em Andamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{isLoading ? '—' : totalEmAndamento}</div>
            <p className="text-xs text-muted-foreground mt-1">Em produção</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Finalizadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{isLoading ? '—' : totalFinalizadas}</div>
            <p className="text-xs text-muted-foreground mt-1">Concluídas</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />Custo Previsto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-foreground">
              {isLoading ? '—' : custoPrevTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Ordens ativas</p>
          </CardContent>
        </Card>
      </div>

      {/* Abas */}
      <Tabs defaultValue="ordens">
        <TabsList>
          <TabsTrigger value="ordens" className="gap-2">
            <Factory className="h-4 w-4" />Ordens
          </TabsTrigger>
          <TabsTrigger value="sugestoes" className="gap-2">
            <Lightbulb className="h-4 w-4" />Sugestões de Produção
          </TabsTrigger>
          <TabsTrigger value="planejamento" className="gap-2">
            <CalendarDays className="h-4 w-4" />Planejamento
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ordens" className="mt-4">
          <TabOrdens filtros={filtros} setFiltros={setFiltros} />
        </TabsContent>

        <TabsContent value="sugestoes" className="mt-4">
          <TabSugestoes />
        </TabsContent>

        <TabsContent value="planejamento" className="mt-4">
          <TabPlanejamento />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PcpOrdens;
